import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { PhysicianGroupType } from "../../../../services/ConfigurationService/types/physicianGroupType";
import { useUpdatePhysicianGroupMutation } from "../../../../services/ConfigurationService";

interface EditPhysicianGroupDialogProps {
  group: PhysicianGroupType;
  open: boolean;
  onClose: () => void;
}

const EditPhysicianGroupDialog: React.FC<EditPhysicianGroupDialogProps> = ({ group, open, onClose }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    contactEmail: "",
    street: "",
    city: "",
    state: "",
    zip: "",
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [updateGroup, { isLoading }] = useUpdatePhysicianGroupMutation();

  useEffect(() => {
    if (group) {
      setForm({
        name: group.name,
        phone: group.phone,
        contactEmail: group.contactEmail ?? "",
        street: group.street,
        city: group.city,
        state: group.state,
        zip: group.zip,
      });
    }
  }, [group]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    try {
      await updateGroup({
        id: group.id,
        company: group.company,
        ...form,
      }).unwrap();
      onClose();
    } catch (err) {
      setErrorMessage("Failed to update physician group.");
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Physician Group</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="ID" value={group.id} disabled fullWidth />
          <TextField label="Company" value={group.company} disabled fullWidth />
          <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
          <TextField label="Phone" name="phone" value={form.phone} onChange={handleChange} fullWidth />
          <TextField label="Contact Email" name="contactEmail" value={form.contactEmail} onChange={handleChange} fullWidth />
          <TextField label="Street" name="street" value={form.street} onChange={handleChange} fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label="City" name="city" value={form.city} onChange={handleChange} fullWidth />
            <TextField label="State" name="state" value={form.state} onChange={handleChange} sx={{ width: 100 }} />
            <TextField label="Zip" name="zip" value={form.zip} onChange={handleChange} sx={{ width: 120 }} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={isLoading || !form.name.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
    <Snackbar
      open={!!errorMessage}
      autoHideDuration={6000}
      onClose={() => setErrorMessage(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert severity="error" onClose={() => setErrorMessage(null)}>
        {errorMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export { EditPhysicianGroupDialog };
